import type { ApiAssistantCmsMediaUploadResponse } from "@/wab/shared/ApiSchema";
import {
  CMS_MEDIA_ID_PATTERN,
  cmsUploadedFileValid,
} from "@/wab/shared/copilot/assistant-cms-draft";
import { z } from "zod";

/** The native CMS upload limit (`/cmse/file-upload`): one file, 8 MiB. No other size, name, type or count rule. */
export const CMS_MEDIA_MAX_FILE_BYTES = 8 * 1024 * 1024;

export const CMS_MEDIA_UPLOAD_KEY_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;

const cmsMediaKind = z.enum(["image", "file"]);

/** The three multipart text fields of an upload; anything else in the form is refused. */
export const assistantCmsMediaUploadFieldsSchema = z
  .object({
    databaseId: z.string().min(1).max(500),
    uploadKey: z.string().regex(CMS_MEDIA_UPLOAD_KEY_PATTERN),
    kind: cmsMediaKind,
  })
  .strict();
export type AssistantCmsMediaUploadFields = z.infer<
  typeof assistantCmsMediaUploadFieldsSchema
>;

const uploadedFile = z.custom<ApiAssistantCmsMediaUploadResponse["file"]>(
  (v) => cmsUploadedFileValid(v, "any"),
  "Invalid native CMS uploaded file",
);

/** What the upload route returns; the client parses it with this exact schema. `file` is the native object, id-free. */
export const assistantCmsMediaUploadResponseSchema = z
  .object({
    mediaId: z.string().regex(CMS_MEDIA_ID_PATTERN),
    kind: cmsMediaKind,
    databaseId: z.string().min(1).max(500),
    file: uploadedFile,
    replayed: z.boolean(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (!cmsUploadedFileValid(value.file, value.kind))
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["file"],
        message: "Uploaded file does not match its kind",
      });
  });
